import React, { useState } from 'react';
import EmailEditor from '../components/EmailEditor/EmailEditor';
import { useCRM } from '../context/CRMContext';

const TYPE_CONFIG = {
  whatsapp: { icon: '💬', color: '#25d366', bg: 'rgba(37,211,102,0.15)', label: 'WhatsApp' },
  email:    { icon: '📧', color: '#3b82f6', bg: 'rgba(59,130,246,0.15)', label: 'Email' },
  sms:      { icon: '📱', color: '#f59e0b', bg: 'rgba(245,158,11,0.15)', label: 'SMS' },
};

const STATUS_CONFIG = {
  draft:  { color: '#64748b', bg: 'rgba(100,116,139,0.15)', label: 'Draft' },
  active: { color: '#3b82f6', bg: 'rgba(59,130,246,0.15)',  label: 'Active' },
  sent:   { color: '#10b981', bg: 'rgba(16,185,129,0.15)',  label: 'Sent' },
  paused: { color: '#f59e0b', bg: 'rgba(245,158,11,0.15)', label: 'Paused' },
};

const TEMPLATES = {
  whatsapp: [
    { id: 't1', name: 'Welcome Message', preview: 'Hi {{name}}! 👋 Thank you for your interest in studying abroad. Our expert counsellors are here to guide you...' },
    { id: 't2', name: 'Intake Reminder', preview: 'Hey {{name}}, the application deadline for {{intake}} is approaching! Apply now to secure your spot at your dream university...' },
    { id: 't3', name: 'Offer Congratulations', preview: 'Congratulations {{name}}! 🎉 You have received an offer from {{university}}. Our team will guide you through the next steps...' },
  ],
  email: [
    { id: 't4', name: 'Study Abroad Guide', preview: 'Subject: Your Complete Guide to Studying in {{country}}...' },
    { id: 't5', name: 'Scholarship Alert', preview: 'Subject: Exclusive Scholarships Available for {{course}} in {{country}}...' },
  ],
  sms: [
    { id: 't6', name: 'Quick Reminder', preview: 'Hi {{name}}, don\'t miss the {{intake}} intake deadline! Reply YES for more info.' },
  ],
};

function CreateCampaignModal({ onClose }) {
  const { addCampaign, leads } = useCRM();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ name: '', type: 'whatsapp', status: 'draft', audience: 'all', templateId: '', message: '', subject: '' });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const audienceCount = form.audience === 'all' ? leads.length
    : form.audience === 'new' ? leads.filter(l => l.status === 'new').length
    : form.audience === 'uk' ? leads.filter(l => l.country === 'UK').length
    : form.audience === 'canada' ? leads.filter(l => l.country === 'Canada').length
    : leads.length;

  const handleCreate = () => {
    addCampaign(form);
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box modal-wide">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: '#0f172a' }}>Create Campaign</h2>
            <p style={{ fontSize: 12, color: '#475569', marginTop: 2 }}>Step {step} of 3</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#475569', cursor: 'pointer', fontSize: 20 }}>✕</button>
        </div>

        <div className="progress-bar" style={{ marginBottom: 24 }}>
          <div className="progress-fill" style={{ width: `${(step / 3) * 100}%`, background: 'linear-gradient(90deg, #7c3aed, #3b82f6)' }} />
        </div>

        {step === 1 && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, color: '#64748b', fontWeight: 500, display: 'block', marginBottom: 8 }}>Campaign Name *</label>
              <input className="crm-input" placeholder="e.g. Sep 2025 Intake Alert" value={form.name} onChange={e => set('name', e.target.value)} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, color: '#64748b', fontWeight: 500, display: 'block', marginBottom: 8 }}>Channel</label>
              <div style={{ display: 'flex', gap: 10 }}>
                {Object.entries(TYPE_CONFIG).map(([key, cfg]) => (
                  <div key={key} onClick={() => set('type', key)}
                    style={{ flex: 1, padding: '14px', borderRadius: 12, border: `2px solid ${form.type === key ? cfg.color : 'rgba(255,255,255,0.08)'}`, background: form.type === key ? cfg.bg : 'rgba(0,0,0,0.03)', cursor: 'pointer', textAlign: 'center', transition: 'all 0.15s' }}>
                    <div style={{ fontSize: 24, marginBottom: 6 }}>{cfg.icon}</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: form.type === key ? cfg.color : '#94a3b8' }}>{cfg.label}</div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <label style={{ fontSize: 12, color: '#64748b', fontWeight: 500, display: 'block', marginBottom: 8 }}>Target Audience</label>
              <select className="crm-select" style={{ width: '100%' }} value={form.audience} onChange={e => set('audience', e.target.value)}>
                <option value="all">All Leads ({leads.length})</option>
                <option value="new">New Leads ({leads.filter(l => l.status === 'new').length})</option>
                <option value="uk">UK Interested ({leads.filter(l => l.country === 'UK').length})</option>
                <option value="canada">Canada Interested ({leads.filter(l => l.country === 'Canada').length})</option>
                <option value="hot">Hot Priority ({leads.filter(l => l.priority === 'hot').length})</option>
              </select>
              <div style={{ fontSize: 12, color: '#7c3aed', marginTop: 6 }}>📊 {audienceCount} leads will receive this campaign</div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div>
            <label style={{ fontSize: 12, color: '#64748b', fontWeight: 500, display: 'block', marginBottom: 8 }}>Choose Template</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
              {(TEMPLATES[form.type] || []).map(t => (
                <div key={t.id} onClick={() => { set('templateId', t.id); set('message', t.preview); }}
                  style={{ padding: 14, borderRadius: 10, border: `2px solid ${form.templateId === t.id ? '#7c3aed' : 'rgba(255,255,255,0.08)'}`, background: form.templateId === t.id ? 'rgba(124,58,237,0.1)' : 'rgba(0,0,0,0.03)', cursor: 'pointer' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: form.templateId === t.id ? '#a78bfa' : '#0f172a', marginBottom: 4 }}>{t.name}</div>
                  <div style={{ fontSize: 11, color: '#64748b', fontStyle: 'italic' }}>{t.preview.substring(0, 80)}...</div>
                </div>
              ))}
              <div onClick={() => { set('templateId', 'custom'); set('message', ''); }}
                style={{ padding: 14, borderRadius: 10, border: `2px dashed ${form.templateId === 'custom' ? '#7c3aed' : 'rgba(255,255,255,0.08)'}`, cursor: 'pointer', textAlign: 'center', color: '#64748b', fontSize: 13 }}>
                ✏️ Write Custom Message
              </div>
            </div>
            {(form.templateId === 'custom' || form.templateId) && (
              <div>
                {form.type === 'email' && (
                  <div style={{ marginBottom: 10 }}>
                    <label style={{ fontSize: 12, color: '#64748b', fontWeight: 500, display: 'block', marginBottom: 6 }}>Subject</label>
                    <input className="crm-input" placeholder="Email subject..." value={form.subject} onChange={e => set('subject', e.target.value)} />
                  </div>
                )}
                <label style={{ fontSize: 12, color: '#64748b', fontWeight: 500, display: 'block', marginBottom: 6 }}>Message</label>
                <textarea className="crm-textarea" placeholder="Type your message... Use {{name}}, {{course}}, {{country}} for personalization"
                  value={form.message} onChange={e => set('message', e.target.value)} style={{ minHeight: 120 }} />
                <div style={{ fontSize: 11, color: '#7c3aed', marginTop: 4 }}>💡 Variables: {'{{name}}'}, {'{{course}}'}, {'{{country}}'}, {'{{intake}}'}</div>
              </div>
            )}
          </div>
        )}

        {step === 3 && (
          <div>
            <div style={{ background: 'rgba(124,58,237,0.08)', border: '1px solid rgba(124,58,237,0.2)', borderRadius: 12, padding: 18, marginBottom: 20 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#a78bfa', marginBottom: 12 }}>Campaign Summary</div>
              {[
                { label: 'Campaign Name', value: form.name },
                { label: 'Channel', value: `${TYPE_CONFIG[form.type]?.icon} ${TYPE_CONFIG[form.type]?.label}` },
                { label: 'Audience', value: `${audienceCount} leads` },
                { label: 'Template', value: form.templateId === 'custom' ? 'Custom message' : TEMPLATES[form.type]?.find(t => t.id === form.templateId)?.name || 'None' },
              ].map(item => (
                <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ fontSize: 12, color: '#64748b' }}>{item.label}</span>
                  <span style={{ fontSize: 12, color: '#0f172a', fontWeight: 500 }}>{item.value || '—'}</span>
                </div>
              ))}
            </div>
            <div>
              <label style={{ fontSize: 12, color: '#64748b', fontWeight: 500, display: 'block', marginBottom: 8 }}>Launch Status</label>
              <div style={{ display: 'flex', gap: 10 }}>
                {[{ v: 'draft', l: 'Save as Draft' }, { v: 'active', l: '🚀 Launch Now' }].map(opt => (
                  <div key={opt.v} onClick={() => set('status', opt.v)}
                    style={{ flex: 1, padding: '12px', borderRadius: 10, border: `2px solid ${form.status === opt.v ? '#7c3aed' : 'rgba(255,255,255,0.08)'}`, background: form.status === opt.v ? 'rgba(124,58,237,0.1)' : 'rgba(0,0,0,0.03)', cursor: 'pointer', textAlign: 'center', fontSize: 13, fontWeight: 600, color: form.status === opt.v ? '#a78bfa' : '#64748b' }}>
                    {opt.l}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 24, justifyContent: 'flex-end' }}>
          {step > 1 && <button className="btn-ghost" onClick={() => setStep(s => s - 1)}>← Back</button>}
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          {step < 3
            ? <button className="btn-primary" onClick={() => setStep(s => s + 1)} disabled={step === 1 && !form.name}>Next →</button>
            : <button className="btn-primary" onClick={handleCreate}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                {form.status === 'active' ? 'Launch Campaign' : 'Save Draft'}
              </button>
          }
        </div>
      </div>
    </div>
  );
}


// ── Email Blast ───────────────────────────────────────────────────────────────
function EmailBlast() {
  const { leads, integrations } = useCRM();
  const sgKey = integrations?.sendgrid?.apiKey || '';

  const [segment,  setSegment]  = useState('all');
  const [subject,  setSubject]  = useState('');
  const [body,     setBody]     = useState('');
  const [fromName, setFromName] = useState('');
  const [fromEmail,setFromEmail]= useState('');
  const [sending,  setSending]  = useState(false);
  const [result,   setResult]   = useState(null);

  const SEGMENTS = [
    { id: 'all',       label: 'All Leads',           fn: () => leads },
    { id: 'new',       label: 'New Enquiry',          fn: () => leads.filter(l => l.status === 'new' || l.status === 'New Enquiry') },
    { id: 'hot',       label: 'Hot Leads',            fn: () => leads.filter(l => l.priority === 'hot') },
    { id: 'no_email',  label: 'Has Email Address',    fn: () => leads.filter(l => l.email) },
    { id: 'facebook',  label: 'From Facebook',        fn: () => leads.filter(l => l.source === 'facebook') },
    { id: 'website',   label: 'From Website',         fn: () => leads.filter(l => l.source === 'website') },
  ];

  const segLeads   = (SEGMENTS.find(s => s.id === segment) || SEGMENTS[0]).fn();
  const recipients = segLeads.filter(l => l.email);
  const noEmail    = segLeads.length - recipients.length;

  const handleSend = async () => {
    if (!subject.trim() || !body.trim() || !fromEmail.trim()) return;
    if (!window.confirm('Send email blast to ' + recipients.length + ' leads? This cannot be undone.')) return;

    setSending(true); setResult(null);

    if (!sgKey) {
      // No SendGrid key — open mailto: for manual send
      const emails = recipients.slice(0, 50).map(l => l.email).join(',');
      const plainBody = body.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
      window.open('mailto:' + emails + '?subject=' + encodeURIComponent(subject) + '&body=' + encodeURIComponent(plainBody));
      setResult({ ok: true, manual: true, count: recipients.length });
      setSending(false);
      return;
    }

    // SendGrid API
    try {
      const res = await fetch('https://api.sendgrid.com/v3/mail/send', {
        method: 'POST',
        headers: { 'Authorization': 'Bearer ' + sgKey, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          personalizations: recipients.map(l => ({ to: [{ email: l.email, name: (l.firstName || '') + ' ' + (l.lastName || '') }] })),
          from: { email: fromEmail, name: fromName || fromEmail },
          subject,
          content: [
            { type: 'text/html', value: body },
            { type: 'text/plain', value: body.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim() },
          ],
        }),
      });
      if (res.status === 202) {
        setResult({ ok: true, count: recipients.length });
      } else {
        const err = await res.json().catch(() => ({}));
        setResult({ ok: false, error: (err.errors || [{ message: 'Unknown error' }])[0].message });
      }
    } catch (e) {
      setResult({ ok: false, error: e.message });
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>

      {/* SendGrid notice */}
      {!sgKey && (
        <div style={{ marginBottom: 20, padding: '14px 18px', borderRadius: 12, background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#d97706', marginBottom: 4 }}>⚡ SendGrid not connected</div>
          <div style={{ fontSize: 12, color: '#92400e', lineHeight: 1.5 }}>
            Without a SendGrid API key, emails will open in your mail client (limited to 50 recipients at a time). To send to all recipients automatically, add your SendGrid API key in <strong>Settings → Integrations</strong>.
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.6fr', gap: 20, alignItems: 'start' }}>

        {/* Left: settings */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          <div className="glass-card" style={{ padding: 18 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 14 }}>👥 Recipients</div>
            {SEGMENTS.map(s => {
              const count = s.fn().filter(l => l.email).length;
              const total = s.fn().length;
              return (
                <div key={s.id} onClick={() => setSegment(s.id)}
                  style={{ padding: '10px 12px', borderRadius: 9, marginBottom: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: '1px solid ' + (segment === s.id ? 'rgba(124,58,237,0.3)' : 'rgba(0,0,0,0.06)'), background: segment === s.id ? 'rgba(124,58,237,0.07)' : 'transparent', transition: 'all 0.12s' }}>
                  <span style={{ fontSize: 13, color: segment === s.id ? '#7c3aed' : '#374151', fontWeight: segment === s.id ? 600 : 400 }}>{s.label}</span>
                  <div style={{ textAlign: 'right' }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: segment === s.id ? '#7c3aed' : '#374151' }}>{count}</span>
                    <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 3 }}>/ {total}</span>
                  </div>
                </div>
              );
            })}
            <div style={{ marginTop: 10, padding: '10px 12px', background: 'rgba(16,185,129,0.07)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: 9 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#059669' }}>{recipients.length} will receive this email</div>
              {noEmail > 0 && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{noEmail} lead{noEmail > 1 ? 's have' : ' has'} no email address and will be skipped</div>}
            </div>
          </div>

          <div className="glass-card" style={{ padding: 18 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 12 }}>📬 Sender Details</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div>
                <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 5 }}>From Name</label>
                <input className="crm-input" value={fromName} onChange={e => setFromName(e.target.value)} placeholder="e.g. Admission Team" style={{ width: '100%' }} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 5 }}>From Email *</label>
                <input className="crm-input" type="email" value={fromEmail} onChange={e => setFromEmail(e.target.value)} placeholder="hello@yourcompany.com" style={{ width: '100%' }} />
              </div>
            </div>
          </div>
        </div>

        {/* Right: compose */}
        <div className="glass-card" style={{ padding: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 14 }}>✉️ Compose Email</div>

          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 6 }}>Subject Line *</label>
            <input className="crm-input" value={subject} onChange={e => setSubject(e.target.value)} placeholder="e.g. Important update about your application" style={{ width: '100%', fontSize: 14 }} />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 8 }}>
              Email Body *
              <span style={{ marginLeft: 8, color: '#94a3b8', fontWeight: 400 }}>Build visually or edit HTML directly</span>
            </label>
            <EmailEditor value={body} onChange={setBody} compact />
          </div>

          {result && (
            <div style={{ marginBottom: 14, padding: '12px 16px', borderRadius: 10, background: result.ok ? 'rgba(16,185,129,0.08)' : 'rgba(239,68,68,0.08)', border: '1px solid ' + (result.ok ? 'rgba(16,185,129,0.25)' : 'rgba(239,68,68,0.25)'), fontSize: 13, color: result.ok ? '#059669' : '#dc2626' }}>
              {result.ok
                ? (result.manual
                  ? '📬 Opened in your mail client for ' + result.count + ' recipients. For automated bulk sending, connect SendGrid in Settings.'
                  : '✅ Email blast sent to ' + result.count + ' recipients via SendGrid!')
                : '❌ Send failed: ' + result.error}
            </div>
          )}

          <button
            className="btn-primary"
            onClick={handleSend}
            disabled={sending || !subject.trim() || !body.trim() || !fromEmail.trim() || recipients.length === 0}
            style={{ width: '100%', padding: '12px', fontSize: 14, fontWeight: 600 }}
          >
            {sending ? 'Sending…' : '🚀 Send Blast to ' + recipients.length + ' Leads'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Campaigns Page ───────────────────────────────────────────────────────
export default function Campaigns() {
  const { campaigns } = useCRM();
  const [showCreate, setShowCreate] = useState(false);
  const [filter,     setFilter]     = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [pageTab,    setPageTab]    = useState('campaigns');

  const filtered = campaigns.filter(c =>
    (filter === 'all' || c.status === filter) &&
    (typeFilter === 'all' || c.type === typeFilter)
  );

  const totalSent    = campaigns.reduce((a, c) => a + (c.sent || 0), 0);
  const totalRead    = campaigns.reduce((a, c) => a + (c.read || 0), 0);
  const avgOpenRate  = totalSent > 0 ? ((totalRead / totalSent) * 100).toFixed(1) : 0;

  return (
    <div className="page-enter" style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

      {/* Stats */}
      <div style={{ padding: '14px 24px', borderBottom: '1px solid rgba(0,0,0,0.05)', display: 'flex', gap: 10, flexShrink: 0 }}>
        {[
          { label: 'Total Campaigns', value: campaigns.length,                                          color: '#7c3aed' },
          { label: 'Total Sent',      value: totalSent,                                                  color: '#3b82f6' },
          { label: 'Avg Open Rate',   value: avgOpenRate + '%',                                          color: '#10b981' },
          { label: 'Active',          value: campaigns.filter(c => c.status === 'active').length,        color: '#f59e0b' },
        ].map(s => (
          <div key={s.label} style={{ flex: 1, background: s.color + '10', border: '1px solid ' + s.color + '25', borderRadius: 10, padding: '10px 14px' }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Page tabs: Campaigns | Email Blast */}
      <div style={{ padding: '10px 24px', borderBottom: '1px solid rgba(0,0,0,0.05)', display: 'flex', gap: 10, alignItems: 'center', flexShrink: 0, flexWrap: 'wrap' }}>
        <div className="tab-bar">
          <div className={'tab-item' + (pageTab === 'campaigns' ? ' active' : '')} onClick={() => setPageTab('campaigns')}>📣 Campaigns</div>
          <div className={'tab-item' + (pageTab === 'blast' ? ' active' : '')} onClick={() => setPageTab('blast')}>📧 Email Blast</div>
        </div>

        {pageTab === 'campaigns' && (
          <>
            <div className="tab-bar" style={{ marginLeft: 8 }}>
              {['all', 'active', 'sent', 'draft'].map(f => (
                <div key={f} className={'tab-item' + (filter === f ? ' active' : '')} onClick={() => setFilter(f)} style={{ textTransform: 'capitalize' }}>{f}</div>
              ))}
            </div>
            <select className="crm-select" value={typeFilter} onChange={e => setTypeFilter(e.target.value)} style={{ height: 34, fontSize: 13 }}>
              <option value="all">All Types</option>
              <option value="whatsapp">💬 WhatsApp</option>
              <option value="email">📧 Email</option>
              <option value="sms">📱 SMS</option>
            </select>
            <button className="btn-primary" style={{ marginLeft: 'auto', fontSize: 13, padding: '7px 16px' }} onClick={() => setShowCreate(true)}>+ New Campaign</button>
          </>
        )}
      </div>

      {/* Email Blast tab */}
      {pageTab === 'blast' && <EmailBlast />}

      {/* Campaigns tab */}
      {pageTab === 'campaigns' && (
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
          {filtered.length === 0 && (
            <div style={{ textAlign: 'center', padding: '80px 0', color: '#94a3b8' }}>
              <div style={{ fontSize: 36, marginBottom: 10 }}>📣</div>
              <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>No campaigns yet</div>
              <div style={{ fontSize: 13, marginBottom: 20 }}>Create your first campaign to engage with your leads.</div>
              <button className="btn-primary" onClick={() => setShowCreate(true)}>+ Create Campaign</button>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px,1fr))', gap: 16 }}>
            {filtered.map(c => {
              const tc   = TYPE_CONFIG[c.type]   || TYPE_CONFIG.whatsapp;
              const sc   = STATUS_CONFIG[c.status] || STATUS_CONFIG.draft;
              const sent = c.sent || 0;
              const read = c.read || 0;
              const openRate = sent > 0 ? ((read / sent) * 100).toFixed(0) : 0;
              return (
                <div key={c.id} className="glass-card" style={{ padding: 20 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14, gap: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 40, height: 40, borderRadius: 10, background: tc.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>{tc.icon}</div>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 2 }}>{c.name}</div>
                        <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: sc.bg, color: sc.color }}>{sc.label}</span>
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 14 }}>
                    {[{ label: 'Sent', value: sent }, { label: 'Opened', value: read }, { label: 'Open Rate', value: openRate + '%' }].map(m => (
                      <div key={m.label} style={{ background: 'rgba(0,0,0,0.03)', borderRadius: 8, padding: '8px 10px', textAlign: 'center' }}>
                        <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a' }}>{m.value}</div>
                        <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>{m.label}</div>
                      </div>
                    ))}
                  </div>
                  {sent === 0 && (
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button className="btn-primary" style={{ fontSize: 12, padding: '7px 14px' }}>🚀 Launch</button>
                      <button className="btn-ghost" style={{ fontSize: 12, padding: '7px 14px' }}>✏️ Edit</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {showCreate && <CreateCampaignModal onClose={() => setShowCreate(false)} />}
    </div>
  );
}
